import { getClient } from '../core/global-config.js';
import { createAndWaitTask } from '../core/index.js';

/**
 * 参数示例:
 * ```typescript
 * {
 *   "prompt": "Smooth transition between two scenes",
 *   "first_frame_img": "https://example.com/first_frame.jpg",
 *   "last_frame_img": "https://example.com/last_frame.jpg",
 *   "duration": 5,
 *   "quality": "720p",
 *   "motion_mode": "normal"
 * }
 * ```
 */
export interface PixverseV5TransitionParams {
  /**
   * 视频生成提示词（可选），描述过渡效果的内容
   */
  prompt?: string;
  /**
   * 首帧图像（与first_frame_image二选一，必填），支持URL或Base64格式，会自动上传到PixVerse
   */
  first_frame_img?: string | string[];
  /**
   * 首帧图像（与first_frame_img二选一，必填），支持URL或Base64格式，会自动上传到PixVerse
   */
  first_frame_image?: string | string[];
  /**
   * 尾帧图像（与last_frame_image二选一，必填），支持URL或Base64格式，会自动上传到PixVerse
   */
  last_frame_img?: string | string[];
  /**
   * 尾帧图像（与last_frame_img二选一，必填），支持URL或Base64格式，会自动上传到PixVerse
   */
  last_frame_image?: string | string[];
  /**
   * 视频时长（秒），V5仅支持5秒，设置其他值会导致验证失败
   * @default 5
   * @values 5
   */
  duration?: 5;
  /**
   * 视频质量（可选），支持: 360p, 540p（默认）, 720p, 1080p
   * @default "540p"
   * @values 360p | 540p | 720p | 1080p
   */
  quality?: '360p' | '540p' | '720p' | '1080p';
  /**
   * 运动模式（可选），支持: normal（标准）, fast（快速），默认normal
   * @default "normal"
   * @values normal | fast
   */
  motion_mode?: 'normal' | 'fast';
  /**
   * 随机种子（可选），用于可重现的生成结果
   */
  seed?: number;
  /**
   * 音效开关（可选）
   */
  sound_effect_switch?: boolean;
  /**
   * 音效内容（可选）
   */
  sound_effect_content?: string;
  /**
   * 唇形同步TTS开关（可选）
   */
  lip_sync_tts_switch?: boolean;
  /**
   * 唇形同步TTS内容（可选）
   */
  lip_sync_tts_content?: string;
  /**
   * 唇形同步TTS说话人ID（可选）
   */
  lip_sync_tts_speaker_id?: string;
}

export interface PixverseV5TransitionResource {
  type: string;
  url: string;
  size?: number;
  jobId?: string;
  [key: string]: any;
}

/**
 * pixverse_v5_transition
 *
 * PixVerse V5 过渡（首尾帧）视频生成接口，支持通过首帧和尾帧图像生成过渡视频。
 * 
 * Model: v5
 * Features:
 * - 通过首尾两帧图像生成平滑过渡视频
 * - 固定5秒视频时长
 * - 支持四种质量等级：360p、540p（默认）、720p、1080p
 * - 支持两种运动模式：normal（标准）、fast（快速）
 * - 支持音效和唇形同步TTS功能
 * - 图片自动上传到PixVerse
 *
 * @example
 * ```typescript
 * import { initSeacloud, pixverseV5Transition } from 'seacloud-sdk';
 *
 * initSeacloud('your-api-key');
 *
 * const result = await pixverseV5Transition(
 *   {
 *     "prompt": "Smooth transition between two scenes",
 *     "first_frame_img": "https://example.com/first_frame.jpg",
 *     "last_frame_img": "https://example.com/last_frame.jpg",
 *     "duration": 5,
 *     "quality": "720p",
 *     "motion_mode": "normal"
 *   }
 * );
 *
 * console.log(result); // 返回生成的资源列表
 * ```
 *
 * @param params 请求参数，详见 PixverseV5TransitionParams 接口定义
 * @returns 生成的资源列表
 */
export async function pixverseV5Transition(
  params: PixverseV5TransitionParams,
): Promise<PixverseV5TransitionResource[]> {
  const client = await getClient();

  const pollingOptions = {
    intervalMs: 3000,
    maxAttempts: 60,
  };

  const result = await createAndWaitTask(
    client,
    '/model/v1/generation',
    {
      model: 'pixverse_v5_transition',
      input: [{ params }],
    },
    pollingOptions,
  );

  const resources: PixverseV5TransitionResource[] = [];

  if (result.output) {
    for (const item of result.output) {
      if (item.content) {
        for (const resource of item.content) {
          resources.push({
            type: resource.type || 'unknown',
            url: resource.url || '',
            size: resource.size,
            jobId: resource.jobId,
            ...resource,
          });
        }
      }
    }
  }

  return resources;
}
